import { useDispatch, useSelector } from "react-redux";
import { useMemo, useEffect } from "react";
import dayjs from "dayjs";
import {
  releaseTemporarySlot,
  selectTimeSlot,
  getAvailableSlots,
  setModalContents,
  clearSession,
  setAppointmentType,
  setShuttleServiceSelected,
  setCurrentlyOpenDateDropdown,
} from "../../store/scheduler";
import "./AppointmentTypeSelector.css";
import { ReactComponent as IconShuttle } from "../../assets/icons/icons-car.svg";
import { ReactComponent as IconSelected } from "../../assets/icons/selected-check.svg";
import TagManager from "react-gtm-module";
import RedirectModal from "../modals/RedirectModal";
import { useNavigate } from "react-router-dom";
import AppointmentTypeIcon from "../AppointmentTypeIcon/AppointmentTypeIcon";

export default function AppointmentTypeSelector() {
  const shuttleLogicEnabled = process.env.REACT_APP_SHUTTLE_LOGIC === "true";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    franchiseName,
    urlSlug,
    services,
    mobileRepair,
    isAfterHoursDropoff,
  } = useSelector((state) => state.scheduler.franchise);
  const {
    selectedServices,
    appointmentType,
    shuttleServiceSelected,
    temporarySlotId,
    recommendedServices,
    selectedSlot,
    franchiseSlots,
  } = useSelector((state) => state.scheduler);
  const env = process.env.REACT_APP_ENV;

  // Redirect if we are ever missing services
  useEffect(() => {
    if (!selectedServices.length && !recommendedServices.length && urlSlug) {
      // history.replace(`/${urlSlug}/`);
      navigate(`/${urlSlug}`, { replace: true });
    }
  }, [urlSlug, selectedServices, recommendedServices]);

  // Re-fetch slots on mount if an appointmentType is already selected (e.g. user
  // navigated back from services screen and changed their selections).
  useEffect(() => {
    if (appointmentType) {
      dispatch(getAvailableSlots());
    }
  }, []);

  const allowStayAndWait = useMemo(() => {
    if (!selectedServices.length && !recommendedServices.length) return false;
    let allowStayAndWait = true;
    for (let i = 0; i < selectedServices.length; i++) {
      const currentService = services[selectedServices[i]];
      if (currentService && !currentService.hasLimitedSlots) {
        allowStayAndWait = false;
        break;
      }
    }
    if (recommendedServices.length) allowStayAndWait = false;
    return allowStayAndWait;
  }, [selectedServices, services, recommendedServices]);

  // Check if shuttle is available for the currently selected time slot
  // When shuttleLogicEnabled is false, always return true (toggle always enabled)
  const shuttleAvailableForSelectedSlot = useMemo(() => {
    if (!shuttleLogicEnabled) return true;
    if (!selectedSlot?.date || !selectedSlot?.time) return true; // No slot selected = toggle enabled
    const daySlots = franchiseSlots[selectedSlot.date];
    const slotData = daySlots?.slots?.find(
      (slot) => slot.fromTime === selectedSlot.time
    );
    return slotData?.shuttleAvailable !== false;
  }, [selectedSlot, franchiseSlots, shuttleLogicEnabled]);

  // Auto-deselect shuttle if slot doesn't support it (only when shuttle logic is enabled)
  useEffect(() => {
    if (
      shuttleLogicEnabled &&
      shuttleServiceSelected &&
      !shuttleAvailableForSelectedSlot
    ) {
      dispatch(setShuttleServiceSelected(false));
    }
  }, [
    shuttleLogicEnabled,
    shuttleAvailableForSelectedSlot,
    shuttleServiceSelected,
    dispatch,
  ]);

  const handleRedirectClick = (e, url) => {
    e.preventDefault();

    if (env !== "TEST") {
      const modal = (
        <RedirectModal
          handleCancel={() => dispatch(setModalContents(null))}
          handleContinue={() => {
            dispatch(releaseTemporarySlot());
            dispatch(clearSession());
            window.location.href = url;
          }}
        />
      );
      dispatch(setModalContents(modal));
    }
  };

  const getServicesString = () => {
    let servicesString = "";
    switch (recommendedServices.length) {
      case 0:
        servicesString = "";
        break;
      case 1:
        servicesString =
          selectedServices.length > 2 ? "" : "recommended service";
        break;
      default:
        servicesString =
          selectedServices.length > 2 ? "" : "recommended services";
    }

    // switch (selectedServices.length) {
    //   case 1:
    //     return `${services[selectedServices[0]].text}.`;
    //   case 2:
    //     return `${services[selectedServices[0]].text} and ${
    //       services[selectedServices[1]].text
    //     }.`;
    //   default:
    //     return "multiple services.";
    // }

    switch (selectedServices.length) {
      case 0:
        servicesString = servicesString + ".";
        break;
      case 1:
        servicesString =
          servicesString +
          (recommendedServices.length ? " and " : "") +
          `${services[selectedServices[0]].text}.`;
        break;
      case 2:
        servicesString =
          servicesString +
          (recommendedServices.length ? `, ` : "") +
          `${services[selectedServices[0]].text} and ${
            services[selectedServices[1]].text
          }.`;
        break;
      default:
        servicesString = servicesString + " multiple services.";
    }
    return servicesString;
  };

  const handleAppointmentTypeToggle = (newAppointmentType) => {
    dispatch(setAppointmentType(newAppointmentType));
    if (temporarySlotId) dispatch(releaseTemporarySlot(temporarySlotId));
    dispatch(selectTimeSlot({ date: "", time: "" }));
    dispatch(setCurrentlyOpenDateDropdown(dayjs().format("YYYY-MM-DD")));

    // Refresh slots because availability changes between appointment types
    dispatch(getAvailableSlots());
  };

  // Effect to automatically select dropoff if we have no stay and wait options
  useEffect(() => {
    // Prevent automatically setting it 'Yes' every time
    if (!selectedServices.length && !recommendedServices.length) return;
    // dispatch(setIsDropoff("Yes"));
    if (!allowStayAndWait && appointmentType === "waiter") {
      dispatch(setAppointmentType("dropoff"));
    }
  }, [selectedServices, allowStayAndWait]);

  const handleShuttleClicked = (e) => {
    e.preventDefault();
    if (!shuttleAvailableForSelectedSlot) return; // Disabled state - do nothing

    const dataLayer = {
      event: "event_select_shuttle",
      shuttleServiceValue: !shuttleServiceSelected,
    };
    TagManager.dataLayer({ dataLayer });

    // Toggle shuttle service selection (no API call needed - shuttleAvailable is already in slot data)
    dispatch(setShuttleServiceSelected(!shuttleServiceSelected));
  };

  // Render selected services summary
  const renderServicesSummary = () => {
    if (selectedServices.length === 0) return null;

    const selectedServicesList = selectedServices.map(
      (index) => services[index]
    );

    return (
      <div className="services-chip-container">
        <div className="services-chip-header">
          <span className="services-chip-label">
            Selected Services or Symptoms
          </span>
          <button
            className="services-summary-edit"
            onClick={() => navigate(`/${urlSlug}/services`)}
          >
            Edit
          </button>
        </div>
        <div className="services-chip-row">
          {selectedServicesList.map((service, idx) => (
            <span key={idx} className="service-chip">
              {service.text}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="service-mode-selector">
      <h1 className="heading">
        I would like to visit the{" "}
        <a
          href={`https://www.cbac.com/${urlSlug}/`}
          className="landing-store"
          onClick={(e) =>
            handleRedirectClick(e, `https://www.cbac.com/${urlSlug}`)
          }
        >
          {franchiseName}
        </a>{" "}
        store.
      </h1>
      {renderServicesSummary()}
      <p className="dropoff-instructions">
        {allowStayAndWait
          ? "Based on the services and/or symptoms you selected, you can choose to wait while we take care of your vehicle."
          : "Based on the services and/or symptoms you selected, we’ll need you to drop off your vehicle."}
      </p>

      <div className="dropoff-icons">
        <AppointmentTypeIcon
          appointmentType={"dropoff"}
          // isDropoff={true}
          allowStayAndWait={allowStayAndWait}
          // selected={isDropoff === "Yes"}
          selected={appointmentType === "dropoff"}
          handleClick={handleAppointmentTypeToggle}
          // disabled={!allowStayAndWait}
        />
        <AppointmentTypeIcon
          appointmentType={"waiter"}
          // isDropoff={false}
          allowStayAndWait={allowStayAndWait}
          // selected={isDropoff === "No"}
          selected={appointmentType === "waiter"}
          handleClick={handleAppointmentTypeToggle}
          disabled={!allowStayAndWait}
        />
        {mobileRepair && (
          <AppointmentTypeIcon
            appointmentType={"mobile"}
            selected={appointmentType === "mobile"}
            handleClick={handleAppointmentTypeToggle}
          />
        )}
        <AppointmentTypeIcon
          appointmentType={"ahdo"}
          selected={appointmentType === "ahdo"}
          handleClick={handleAppointmentTypeToggle}
          disabled={!isAfterHoursDropoff}
        />
      </div>

      {appointmentType === "dropoff" ? (
        <>
          <button
            className={`dropoff-shuttle-container${
              shuttleServiceSelected ? " selected" : ""
            }${!shuttleAvailableForSelectedSlot ? " disabled" : ""}`}
            onClick={handleShuttleClicked}
            disabled={!shuttleAvailableForSelectedSlot}
            role="switch"
            aria-checked={shuttleServiceSelected}
            aria-label="Shuttle Service"
          >
            <div className="shuttle-left">
              <div className="shuttle-icon-wrapper">
                {shuttleServiceSelected ? (
                  <IconSelected className="shuttle-svg-icon" />
                ) : (
                  <IconShuttle className="shuttle-svg-icon" />
                )}
              </div>
            </div>
            <div className="shuttle-right">
              <span className="shuttle-right-title">Shuttle Service</span>
              <p className="shuttle-right-description">
                {!shuttleAvailableForSelectedSlot
                  ? "Unavailable for current time slot selection"
                  : "Select complimentary shuttle service during normal business hours."}
              </p>
            </div>
          </button>
          {shuttleLogicEnabled && (
            <p className="shuttle-helper-text">
              <span className="shuttle-badge-preview"></span>
              Indicates shuttle service is available for this time slot
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}
